import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiImage, FiMaximize2, FiPlus, FiRefreshCw, FiSearch, FiShoppingBag, FiX } from 'react-icons/fi';
import { JewelryService } from '../services/ZashaniService';
import { TableEmptyState } from '../components/LoadingStates';
import { BASE_URL } from '../utils/Constants';

const currency = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' });
const CATALOG_PAGE_SIZE = 24;
const categoryOrder = ['anillos', 'collares', 'pulseras', 'aretes', 'conjuntos', 'varios'];
const categoryLabels = {
    anillos: 'Anillos',
    collares: 'Collares',
    pulseras: 'Pulseras',
    aretes: 'Aretes',
    conjuntos: 'Conjuntos',
    varios: 'Varios',
};

const normalizeCategoryKey = (value = '') => {
    const normalized = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    if (normalized.startsWith('anillo')) return 'anillos';
    if (normalized.startsWith('collar')) return 'collares';
    if (normalized.startsWith('pulsera')) return 'pulseras';
    if (normalized.startsWith('arete')) return 'aretes';
    if (normalized.startsWith('conjunto')) return 'conjuntos';
    if (normalized.startsWith('vario')) return 'varios';
    return normalized || 'otros';
};

const buildCategoryGroups = (categories = []) => {
    const groups = categories.reduce((acc, category) => {
        const key = normalizeCategoryKey(category);
        if (!acc[key]) {
            acc[key] = { key, label: categoryLabels[key] || category, values: [] };
        }
        if (!acc[key].values.includes(category)) {
            acc[key].values.push(category);
        }
        return acc;
    }, {});

    return Object.values(groups).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.key);
        const bIndex = categoryOrder.indexOf(b.key);
        if (aIndex !== -1 || bIndex !== -1) {
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
        }
        return a.label.localeCompare(b.label, 'es');
    });
};

const PublicCatalog = () => {
    const [catalog, setCatalog] = useState({ brand: 'Zashani', categories: [], items: [], total: 0, page: 1, pages: 1 });
    const [filters, setFilters] = useState({ search: '', category: '', categoryValues: [] });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [message, setMessage] = useState('');
    const [previewItem, setPreviewItem] = useState(null);

    const load = useCallback(async ({ page = 1, append = false, nextFilters = filters } = {}) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setMessage('');
        try {
            const data = await JewelryService.publicCatalog({
                search: nextFilters.search || undefined,
                categories: nextFilters.categoryValues?.length ? nextFilters.categoryValues.join(',') : undefined,
                page,
                limit: CATALOG_PAGE_SIZE,
            });
            setCatalog((current) => ({
                ...data,
                categories: data.categories || current.categories || [],
                items: append ? [...(current.items || []), ...(data.items || [])] : (data.items || []),
                total: data.total || 0,
                page: data.page || page,
                pages: data.pages || 1,
            }));
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar el catalogo.');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const submit = (event) => {
        event.preventDefault();
        setFilters((current) => ({ ...current, search: searchTerm.trim() }));
    };

    const categoryGroups = useMemo(() => buildCategoryGroups(catalog.categories), [catalog.categories]);

    const selectCategory = (group) => {
        setFilters((current) => ({
            ...current,
            category: group?.label || '',
            categoryValues: group?.values || [],
        }));
    };

    const loadMore = () => {
        if (catalog.page >= catalog.pages || loadingMore) return;
        load({ page: catalog.page + 1, append: true });
    };

    const imageSrc = (item) => {
        if (item.image?.dataUrl) return item.image.dataUrl;
        if (item.imageUrl) return `${BASE_URL}${item.imageUrl}`;
        return '';
    };

    const featured = useMemo(() => catalog.items.filter((item) => item.catalogFeatured), [catalog.items]);
    const heroItem = featured.find((item) => imageSrc(item)) || catalog.items.find((item) => imageSrc(item));
    const heroItems = catalog.items.filter((item) => imageSrc(item)).slice(0, 3);
    const activeCategoryLabel = filters.category || 'Todo';
    const loadedLabel = `${catalog.items.length} de ${catalog.total || catalog.items.length}`;
    const heroStats = [
        { label: 'Coleccion', value: activeCategoryLabel },
        { label: 'Piezas', value: catalog.total || catalog.items.length },
        { label: 'Categorias', value: categoryGroups.length },
    ];

    const renderCatalogImage = (item) => (
        <button
            className="catalog-image"
            type="button"
            onClick={() => imageSrc(item) && setPreviewItem(item)}
            disabled={!imageSrc(item)}
            aria-label={imageSrc(item) ? `Ver imagen de ${item.code}` : `Producto ${item.code} sin imagen`}
        >
            {imageSrc(item) ? (
                <>
                    <img src={imageSrc(item)} alt={item.code} loading="lazy" />
                    <span className="catalog-zoom-hint"><FiMaximize2 /> Ampliar</span>
                </>
            ) : (
                <FiImage />
            )}
        </button>
    );

    return (
        <main className="public-catalog-page">
            <nav className="catalog-nav">
                <div className="catalog-nav-brand">
                    <img src="/brand/zashani-logo-wide-header.png" alt="Zashani" />
                    <span>Catalogo virtual</span>
                </div>
                <div className="catalog-nav-links">
                    <a href="#catalog-products">Coleccion</a>
                    <a href="https://wa.me/" target="_blank" rel="noreferrer">WhatsApp</a>
                </div>
            </nav>

            <section
                className="catalog-hero"
                style={heroItem ? { '--hero-image': `url("${imageSrc(heroItem)}")` } : undefined}
            >
                <div className="catalog-hero-copy">
                    <p className="eyebrow">Catalogo virtual</p>
                    <h1>Joyas Zashani</h1>
                    <p>Piezas listas para elegir, acercar y consultar. Una vitrina limpia, rapida y pensada para comprar sin complicarse.</p>
                    <div className="catalog-hero-actions">
                        <a className="primary-button" href="#catalog-products">
                            <span>Ver coleccion</span>
                            <FiArrowRight />
                        </a>
                        <a className="ghost-button catalog-whatsapp" href="https://wa.me/" target="_blank" rel="noreferrer">
                            <FiShoppingBag />
                            <span>Consultar ahora</span>
                        </a>
                    </div>
                    <div className="catalog-hero-stats">
                        {heroStats.map((stat) => (
                            <span key={stat.label}>
                                <strong>{stat.value}</strong>
                                {stat.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="catalog-hero-showcase" aria-hidden="true">
                    {heroItems.length > 0 ? heroItems.map((item, index) => (
                        <div className={`hero-product-shot shot-${index + 1}`} key={item._id} style={{ '--shot-delay': `${index * 120}ms`, '--float-delay': `${index * 900}ms` }}>
                            <img src={imageSrc(item)} alt="" />
                            <span>{item.category || 'Joyeria'}</span>
                        </div>
                    )) : (
                        <div className="hero-product-shot empty-shot">
                            <FiImage />
                            <span>Zashani</span>
                        </div>
                    )}
                </div>
            </section>

            <section className="catalog-bar">
                <form className="catalog-filter" onSubmit={submit}>
                    <div className="catalog-filter-label">
                        <span>{activeCategoryLabel}</span>
                        <small>{loadedLabel} piezas cargadas</small>
                    </div>
                    <label>
                        <FiSearch />
                        <input placeholder="Buscar por codigo, categoria o descripcion" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                    </label>
                    <button className="primary-button" type="submit" disabled={loading}>
                        <FiRefreshCw />
                        <span>Actualizar</span>
                    </button>
                </form>
                <div className="catalog-category-strip" aria-label="Categorias del catalogo">
                    <button className={!filters.category ? 'active' : ''} type="button" onClick={() => selectCategory(null)}>Todo</button>
                    {categoryGroups.map((group) => (
                        <button
                            className={filters.category === group.label ? 'active' : ''}
                            key={group.key}
                            type="button"
                            onClick={() => selectCategory(group)}
                        >
                            {group.label}
                        </button>
                    ))}
                </div>
            </section>

            {message && <div className="catalog-notice">{message}</div>}

            {featured.length > 0 && (
                <section className="catalog-section">
                    <div className="catalog-section-title">
                        <p className="eyebrow">Destacados</p>
                        <h2>Piezas seleccionadas</h2>
                    </div>
                    <div className="catalog-featured-grid">
                        {featured.slice(0, 4).map((item, index) => (
                            <article className="catalog-card featured" key={item._id} style={{ '--card-delay': `${index * 70}ms` }}>
                                {renderCatalogImage(item)}
                                <div>
                                    <span>{item.category || 'Joyeria'}</span>
                                    <h3>{item.code}</h3>
                                    <p>{item.description || 'Pieza Zashani disponible.'}</p>
                                    <strong>{currency.format(item.suggestedPrice || 0)}</strong>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            )}

            <section className="catalog-section" id="catalog-products">
                <div className="catalog-section-title">
                    <p className="eyebrow">Disponibles</p>
                    <h2>{catalog.total || catalog.items.length} producto{(catalog.total || catalog.items.length) === 1 ? '' : 's'}</h2>
                    <span>{catalog.items.length} cargado{catalog.items.length === 1 ? '' : 's'}{filters.category ? ` en ${filters.category}` : ''}</span>
                </div>
                {loading || catalog.items.length === 0 ? (
                    <TableEmptyState loading={loading} emptyText="No hay productos publicados en este momento." />
                ) : (
                    <>
                        <div className="catalog-grid">
                            {catalog.items.map((item, index) => (
                                <article className="catalog-card" key={item._id} style={{ '--card-delay': `${Math.min(index, 10) * 45}ms` }}>
                                    {renderCatalogImage(item)}
                                    <div className="catalog-card-body">
                                        <div className="catalog-card-topline">
                                            <span>{item.category || 'Joyeria'}</span>
                                            {item.catalogFeatured && <small>Destacado</small>}
                                        </div>
                                        <h3>{item.description || 'Pieza Zashani'}</h3>
                                        <small className="catalog-code">{item.code}</small>
                                        <p>Disponible para consulta inmediata y coordinacion directa.</p>
                                        <div className="catalog-card-footer">
                                            <strong>{currency.format(item.suggestedPrice || 0)}</strong>
                                            <small>{item.stock} disponible{item.stock === 1 ? '' : 's'}</small>
                                        </div>
                                        <a className="catalog-action" href={`https://wa.me/?text=${encodeURIComponent(`Hola, quiero consultar por la pieza Zashani ${item.code}`)}`} target="_blank" rel="noreferrer">
                                            <FiShoppingBag />
                                            <span>Consultar pieza</span>
                                        </a>
                                    </div>
                                </article>
                            ))}
                        </div>
                        {catalog.page < catalog.pages && (
                            <div className="catalog-load-more">
                                <button className="primary-button" type="button" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? <FiRefreshCw className="is-spinning" /> : <FiPlus />}
                                    <span>{loadingMore ? 'Cargando...' : 'Cargar mas'}</span>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
            {previewItem && (
                <div className="catalog-lightbox-backdrop" role="presentation" onClick={() => setPreviewItem(null)}>
                    <article className="catalog-lightbox" role="dialog" aria-modal="true" aria-labelledby="catalog-lightbox-title" onClick={(event) => event.stopPropagation()}>
                        <button className="modal-close-button" type="button" onClick={() => setPreviewItem(null)} aria-label="Cerrar imagen">
                            <FiX />
                        </button>
                        <div className="catalog-lightbox-image">
                            <img src={imageSrc(previewItem)} alt={previewItem.code} />
                        </div>
                        <aside className="catalog-lightbox-info">
                            <span>{previewItem.category || 'Joyeria'}</span>
                            <h2 id="catalog-lightbox-title">{previewItem.code}</h2>
                            <p>{previewItem.description || 'Pieza Zashani disponible.'}</p>
                            <strong>{currency.format(previewItem.suggestedPrice || 0)}</strong>
                            <small>{previewItem.stock} disponible{previewItem.stock === 1 ? '' : 's'}</small>
                            <a className="catalog-action" href={`https://wa.me/?text=${encodeURIComponent(`Hola, quiero consultar por la pieza Zashani ${previewItem.code}`)}`} target="_blank" rel="noreferrer">
                                <FiShoppingBag />
                                <span>Consultar esta pieza</span>
                            </a>
                        </aside>
                    </article>
                </div>
            )}
        </main>
    );
};

export default PublicCatalog;
